import { useState } from "react";
import {
  Form,
  FormLayout,
  TextField,
  Select,
  Button,
  Banner,
  BlockStack,
  Text,
} from "@shopify/polaris";
import { useApi } from "../hooks/useApi";
import { useTranslation } from "../hooks/useTranslation";

interface FeedbackFormData {
  type: string;
  title: string;
  description: string;
  priority: string;
  email: string;
}

export function FeedbackForm({ shop }: { shop: string | null }) {
  const { t } = useTranslation();
  const { Call } = useApi();
  const [formData, setFormData] = useState<FeedbackFormData>({
    type: "bug",
    title: "",
    description: "",
    priority: "medium",
    email: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typeOptions = [
    { label: t("dashboard.feedback.fields.type.options.bug"), value: "bug" },
    { label: t("dashboard.feedback.fields.type.options.feature"), value: "feature" },
    { label: t("dashboard.feedback.fields.type.options.general"), value: "general" },
  ];

  const priorityOptions = [
    { label: "Low", value: "low" },
    { label: "Medium", value: "medium" },
    { label: "High", value: "high" },
  ];

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      setError(t("dashboard.feedback.requiredError"));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await Call("/feedback", "POST", formData);

      if (response?.success) {
        setSuccess(true);
        // Reset form
        setFormData({
          type: "bug",
          title: "",
          description: "",
          priority: "medium",
          email: "",
        });
      } else {
        setError(response?.error || "Failed to submit feedback");
      }
    } catch (err) {
      setError("An error occurred while submitting your feedback");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BlockStack gap="500">
      <BlockStack gap="100">
        <Text variant="headingMd" as="h2">
          {t("dashboard.feedback.title")}
        </Text>
        {/* Added 'as="p"' here to fix TS2741 */}
        <Text variant="bodyMd" as="p" tone="subdued">
          {t("dashboard.feedback.subtitle")}
        </Text>
      </BlockStack>

      {success && (
        <Banner tone="success" onDismiss={() => setSuccess(false)}>
          {t("dashboard.feedback.success")}
        </Banner>
      )}
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Form onSubmit={handleSubmit}>
        <FormLayout>
          <Select
            label={t("dashboard.feedback.fields.type.label")}
            options={typeOptions}
            value={formData.type}
            onChange={(value) => setFormData({ ...formData, type: value })}
          />

          <TextField
            label={t("dashboard.feedback.fields.title.label")}
            value={formData.title}
            onChange={(value) => setFormData({ ...formData, title: value })}
            placeholder={t("dashboard.feedback.fields.title.placeholder")}
            autoComplete="off"
            requiredIndicator
          />

          <TextField
            label={t("dashboard.feedback.fields.description.label")}
            value={formData.description}
            onChange={(value) =>
              setFormData({ ...formData, description: value })
            }
            placeholder={t("dashboard.feedback.fields.description.placeholder")}
            multiline={4}
            autoComplete="off"
            requiredIndicator
          />

          {/*<Select
            label="Priority"
            options={priorityOptions}
            value={formData.priority}
            onChange={(value) => setFormData({ ...formData, priority: value })}
          />*/}

          <TextField
            label={t("dashboard.feedback.fields.email.label")}
            type="email"
            value={formData.email}
            onChange={(value) => setFormData({ ...formData, email: value })}
            placeholder={t("dashboard.feedback.fields.email.placeholder")}
            autoComplete="email"
            helpText={t("dashboard.feedback.fields.email.helpText")}
          />

          <Button
            submit
            variant="primary"
            loading={loading}
            disabled={!formData.title || !formData.description}
          >
            {t("dashboard.feedback.submit")}
          </Button>
        </FormLayout>
      </Form>
    </BlockStack>
  );
}
